import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import AddLocationAltOutlinedIcon from '@mui/icons-material/AddLocationAltOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import StarOutlineOutlinedIcon from '@mui/icons-material/StarOutlineOutlined'
import PropTypes from 'prop-types'
import { theme } from '../../theme'
import { useUser } from '../../hooks/useUser'
import {
  buildDeliveryAddressSummary,
  buildDeliveryProfileValidationMessage,
  buildDeliveryProfilePayload,
  createEmptyDeliveryProfileForm,
  getDefaultDeliveryProfileId,
  getMissingRequiredDeliveryFields,
  normalizeManagedDeliveryProfiles
} from '../../utils/deliveryProfiles'

const defaultFormState = (userData = {}) => createEmptyDeliveryProfileForm({
  username: userData.username || '',
  phone: userData.phone || '',
  company: userData.company || '',
  city: userData.company_profile?.city || '',
  county: userData.company_profile?.county || '',
  postal_code: userData.company_profile?.postal_code || ''
})

const REQUIRED_FIELD_HELPERS = {
  recipient_name: 'Completează numele destinatarului.',
  recipient_phone: 'Completează telefonul destinatarului.',
  line1: 'Completează adresa de livrare.',
  city: 'Completează orașul.',
  county: 'Completează județul.',
  postal_code: 'Completează codul poștal.'
}

const buildFieldErrors = (form) => {
  const payload = buildDeliveryProfilePayload(form)
  return getMissingRequiredDeliveryFields(payload).reduce((errors, field) => {
    errors[field] = REQUIRED_FIELD_HELPERS[field] || 'Câmp obligatoriu.'
    return errors
  }, {})
}

const DeliveryProfilesManager = ({ userData }) => {
  const { handleUpdateUserProfile } = useUser()
  const savedProfiles = useMemo(() => normalizeManagedDeliveryProfiles(userData), [userData])
  const defaultProfileId = useMemo(
    () => getDefaultDeliveryProfileId(userData, savedProfiles),
    [savedProfiles, userData]
  )
  const hasValidSavedProfile = useMemo(
    () => savedProfiles.some(profile => profile.is_valid),
    [savedProfiles]
  )
  const [editingProfileId, setEditingProfileId] = useState('')
  const [addressForm, setAddressForm] = useState(defaultFormState(userData))
  const [setAsDefault, setSetAsDefault] = useState(!hasValidSavedProfile)
  const [loading, setLoading] = useState(false)
  const [feedback, setFeedback] = useState({ type: '', message: '' })
  const [fieldErrors, setFieldErrors] = useState({})

  useEffect(() => {
    if (!editingProfileId) {
      setAddressForm(defaultFormState(userData))
      setSetAsDefault(!hasValidSavedProfile)
      setFieldErrors({})
      return
    }

    const profile = savedProfiles.find(item => item.id === editingProfileId)
    if (!profile) {
      setEditingProfileId('')
      setAddressForm(defaultFormState(userData))
      setSetAsDefault(!hasValidSavedProfile)
      setFieldErrors({})
      return
    }

    setAddressForm(createEmptyDeliveryProfileForm(profile))
    setSetAsDefault(profile.id === defaultProfileId)
    setFieldErrors({})
  }, [defaultProfileId, editingProfileId, hasValidSavedProfile, savedProfiles, userData])

  const resetEditor = () => {
    setEditingProfileId('')
    setAddressForm(defaultFormState(userData))
    setSetAsDefault(!hasValidSavedProfile)
    setFieldErrors({})
  }

  const handleFieldChange = (field, value) => {
    setAddressForm(prev => ({ ...prev, [field]: value }))
    setFieldErrors(prev => {
      if (!prev[field]) {
        return prev
      }
      const next = { ...prev }
      delete next[field]
      return next
    })
  }

  const submitAddress = async () => {
    const nextFieldErrors = buildFieldErrors(addressForm)
    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors)
      setFeedback({
        type: 'error',
        message: buildDeliveryProfileValidationMessage(Object.keys(nextFieldErrors))
      })
      return
    }

    setLoading(true)
    setFeedback({ type: '', message: '' })

    try {
      const payload = {
        address_action: editingProfileId ? 'update' : 'create',
        address_id: editingProfileId || undefined,
        set_as_default: setAsDefault,
        address: buildDeliveryProfilePayload(addressForm, editingProfileId)
      }
      const message = await handleUpdateUserProfile(payload)
      setFeedback({ type: 'success', message: message || 'Adresa a fost salvată.' })
      resetEditor()
    } catch (error) {
      setFeedback({ type: 'error', message: error.message || 'Adresa nu a putut fi salvată.' })
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (profile) => {
    setFeedback({ type: '', message: '' })
    setEditingProfileId(profile.id)
    setAddressForm(createEmptyDeliveryProfileForm(profile))
    setSetAsDefault(profile.id === defaultProfileId)
    setFieldErrors({})
  }

  const handleDelete = async (profile) => {
    if (!window.confirm(`Ștergi adresa „${profile.display_name}”?`)) {
      return
    }

    setLoading(true)
    setFeedback({ type: '', message: '' })
    try {
      const message = await handleUpdateUserProfile({
        address_action: 'delete',
        address_id: profile.id
      })
      setFeedback({ type: 'success', message: message || 'Adresa a fost ștearsă.' })
      if (editingProfileId === profile.id) {
        resetEditor()
      }
    } catch (error) {
      setFeedback({ type: 'error', message: error.message || 'Adresa nu a putut fi ștearsă.' })
    } finally {
      setLoading(false)
    }
  }

  const handleSetDefault = async (profileId) => {
    if (profileId === defaultProfileId) {
      return
    }

    setLoading(true)
    setFeedback({ type: '', message: '' })
    try {
      const message = await handleUpdateUserProfile({
        address_action: 'set_default',
        address_id: profileId
      })
      setFeedback({ type: 'success', message: message || 'Adresa implicită a fost actualizată.' })
    } catch (error) {
      setFeedback({ type: 'error', message: error.message || 'Adresa implicită nu a putut fi actualizată.' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ width: '100%', marginTop: '30px', padding: '20px', minHeight: { md: '50vh' } }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant='h5' sx={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>
            Adrese de livrare
          </Typography>
          <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium }}>
            Gestionează adresele alternative de livrare. Checkout-ul va afișa doar aceste adrese salvate.
          </Typography>
        </Box>

        {feedback.message && (
          <Alert severity={feedback.type === 'success' ? 'success' : 'error'}>
            {feedback.message}
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: theme.palette.borderColor.main,
            borderRadius: '16px',
            padding: '20px'
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent='space-between' spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
            <Box>
              <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>
                {editingProfileId ? 'Editează adresa' : 'Adaugă adresă nouă'}
              </Typography>
              <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium }}>
                Poți seta o singură adresă implicită. O poți schimba oricând din listă.
              </Typography>
            </Box>
            {!editingProfileId && (
              <Button
                variant='outlined'
                color='accent'
                startIcon={<AddLocationAltOutlinedIcon />}
                onClick={() => {
                  setFeedback({ type: '', message: '' })
                  setEditingProfileId('')
                  setAddressForm(defaultFormState(userData))
                  setSetAsDefault(savedProfiles.length === 0)
                }}
                sx={{ alignSelf: { xs: 'stretch', md: 'auto' }, textTransform: 'none', borderRadius: '14px' }}
              >
                Formular nou
              </Button>
            )}
          </Stack>

          <Stack spacing={2} mt={3}>
            <TextField size='small' label='Etichetă adresă (opțional)' value={addressForm.label} onChange={(event) => handleFieldChange('label', event.target.value)} />
            <TextField size='small' label='Nume destinatar' value={addressForm.recipient_name} onChange={(event) => handleFieldChange('recipient_name', event.target.value)} error={Boolean(fieldErrors.recipient_name)} helperText={fieldErrors.recipient_name || ' '} />
            <TextField size='small' label='Telefon destinatar' value={addressForm.recipient_phone} onChange={(event) => handleFieldChange('recipient_phone', event.target.value)} error={Boolean(fieldErrors.recipient_phone)} helperText={fieldErrors.recipient_phone || ' '} />
            <TextField size='small' label='Companie / punct de lucru' value={addressForm.company} onChange={(event) => handleFieldChange('company', event.target.value)} />
            <TextField size='small' label='Adresă livrare' value={addressForm.line1} onChange={(event) => handleFieldChange('line1', event.target.value)} error={Boolean(fieldErrors.line1)} helperText={fieldErrors.line1 || ' '} />
            <TextField size='small' label='Adresă livrare (completări)' value={addressForm.line2} onChange={(event) => handleFieldChange('line2', event.target.value)} />
            <TextField size='small' label='Oraș' value={addressForm.city} onChange={(event) => handleFieldChange('city', event.target.value)} error={Boolean(fieldErrors.city)} helperText={fieldErrors.city || ' '} />
            <TextField size='small' label='Județ' value={addressForm.county} onChange={(event) => handleFieldChange('county', event.target.value)} error={Boolean(fieldErrors.county)} helperText={fieldErrors.county || ' '} />
            <TextField size='small' label='Cod poștal' value={addressForm.postal_code} onChange={(event) => handleFieldChange('postal_code', event.target.value)} error={Boolean(fieldErrors.postal_code)} helperText={fieldErrors.postal_code || ' '} />
            <FormControlLabel
              control={
                <Checkbox
                  checked={setAsDefault}
                  onChange={(event) => setSetAsDefault(event.target.checked)}
                  color='error'
                />
              }
              label='Setează ca adresă implicită'
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent='flex-end'>
              {editingProfileId && (
                <Button
                  variant='outlined'
                  color='accent'
                  onClick={resetEditor}
                  sx={{ textTransform: 'none', borderRadius: '12px' }}
                >
                  Renunță
                </Button>
              )}
              <Button
                variant='contained'
                color='accent'
                onClick={submitAddress}
                disabled={loading}
                sx={{ color: '#FFFFFF', textTransform: 'none', borderRadius: '12px' }}
              >
                {loading ? 'Se salvează...' : editingProfileId ? 'Actualizează adresa' : 'Salvează adresa'}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: theme.palette.borderColor.main,
            borderRadius: '16px',
            padding: '20px'
          }}
        >
          <Typography sx={{ fontSize: '18px', fontWeight: 700, marginBottom: '6px' }}>
            Adrese salvate
          </Typography>
          <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium, marginBottom: '20px' }}>
            Adresa implicită apare prima în checkout. Poți avea mai multe adrese alternative.
          </Typography>

          {savedProfiles.length === 0 && (
            <Alert severity='info'>Nu ai salvat încă nicio adresă alternativă de livrare.</Alert>
          )}

          {savedProfiles.length > 0 && (
            <Stack spacing={2}>
              {savedProfiles.map(profile => {
                const isDefault = profile.id === defaultProfileId
                return (
                  <Paper
                    key={profile.id}
                    elevation={0}
                    sx={{
                      border: '1px solid',
                      borderColor: isDefault ? theme.palette.accent.main : theme.palette.borderColor.main,
                      borderRadius: '16px',
                      padding: '18px'
                    }}
                  >
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent='space-between' spacing={2}>
                      <Stack spacing={1}>
                        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
                          <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>
                            {profile.display_name}
                          </Typography>
                          {isDefault && <Chip size='small' color='error' label='Implicită' />}
                          {!profile.is_valid && <Chip size='small' color='warning' label='Necesită completare' />}
                        </Stack>
                        <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium }}>
                          {profile.company}
                        </Typography>
                        <Stack direction='row' spacing={1} alignItems='flex-start'>
                          <LocationOnOutlinedIcon sx={{ fontSize: '18px', color: theme.palette.fontColor.medium, marginTop: '2px' }} />
                          <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium }}>
                            {buildDeliveryAddressSummary(profile)}
                          </Typography>
                        </Stack>
                        {profile.recipient_phone && (
                          <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium }}>
                            {profile.recipient_phone}
                          </Typography>
                        )}
                        {!profile.is_valid && (
                          <Alert severity='warning' sx={{ mt: 1 }}>
                            {buildDeliveryProfileValidationMessage(profile.missing_required_fields)}
                          </Alert>
                        )}
                      </Stack>

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                        {!isDefault && (
                          <Button
                            variant='outlined'
                            color='accent'
                            startIcon={<StarOutlineOutlinedIcon />}
                            onClick={() => handleSetDefault(profile.id)}
                            disabled={!profile.is_valid}
                            sx={{ textTransform: 'none', borderRadius: '12px' }}
                          >
                            Setează implicită
                          </Button>
                        )}
                        <Button
                          variant='outlined'
                          color='accent'
                          startIcon={<EditOutlinedIcon />}
                          onClick={() => handleEdit(profile)}
                          sx={{ textTransform: 'none', borderRadius: '12px' }}
                        >
                          Editează
                        </Button>
                        <Button
                          variant='outlined'
                          color='accent'
                          startIcon={<DeleteOutlineOutlinedIcon />}
                          onClick={() => handleDelete(profile)}
                          sx={{ textTransform: 'none', borderRadius: '12px' }}
                        >
                          Șterge
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                )
              })}
            </Stack>
          )}
        </Paper>
      </Stack>
    </Box>
  )
}

DeliveryProfilesManager.propTypes = {
  userData: PropTypes.object
}

export default DeliveryProfilesManager
